import {ScratchStorage, Asset} from '@scratch/scratch-storage';

import defaultProject from './default-project';
import {GUIStorage, TranslatorFunction, VirtualMachine, GUICloudVariableProvider} from '../gui-config';
import {LegacyBackpackStorage} from './legacy-backpack-storage';
import CloudProvider from './cloud-provider';

import saveProjectToServer from '../lib/save-project-to-server';
import {buildLibraryAssetUrl} from './legacy-library-asset-url';

export class LegacyStorage implements GUIStorage {
    private projectHost?: string;
    private projectToken?: string;
    private assetHost?: string;
    private translator?: TranslatorFunction;

    readonly scratchStorage = new ScratchStorage();
    readonly backpackStorage = new LegacyBackpackStorage({
        readAuth (session) {
            if (!session) {
                return Promise.reject(new Error('missing session'));
            }

            return Promise.resolve({
                username: session.username,
                authType: 'x-token',
                authToken: session.token
            });
        }
    });
    readonly cloudVariables = {
        createProvider (
            cloudHost: string,
            vm: VirtualMachine,
            username: string,
            projectId: string
        ): GUICloudVariableProvider {
            return new CloudProvider(cloudHost, vm, username, projectId);
        }
    };

    constructor () {
        this.cacheDefaultProject(this.scratchStorage);
        this.addOfficialScratchWebStores(this.scratchStorage);
    }

    setProjectHost (host: string): void {
        this.projectHost = host;
    }

    setProjectToken (token: string): void {
        this.projectToken = token;
    }

    setProjectMetadata (projectId: string | null | undefined): void {
        const {RequestMetadata, setMetadata, unsetMetadata} = this.scratchStorage.scratchFetch;

        // If project ID is '0' or zero, it's not a real project ID. In that case, remove the project ID metadata.
        // Same if it's null undefined.
        if (projectId && projectId !== '0') {
            setMetadata(RequestMetadata.ProjectId, projectId);
        } else {
            unsetMetadata(RequestMetadata.ProjectId);
        }
    }

    setAssetHost (host: string): void {
        this.assetHost = host;
    }

    getLibraryAssetUrl (assetId: string, dataFormat: string): string {
        // Currently defaults to the legacy library asset host.
        // Consider changing this to use the asset host if we want to start serving library assets
        // from the asset host instead
        return buildLibraryAssetUrl(assetId, dataFormat);
    }

    setTranslatorFunction (translator: TranslatorFunction): void {
        this.translator = translator;

        this.cacheDefaultProject(this.scratchStorage);
    }

    setBackpackHost (host: string): void {
        this.backpackStorage.setHostAndRegisterWebStore(host, this.scratchStorage);
    }

    saveProject (
        projectId: number,
        vmState: string,
        params: {originalId: string; isCopy: boolean; isRemix: boolean; title: string;}
    ): Promise<{id: string | number;}> {
        const host = this.projectHost;

        if (!host) {
            return Promise.reject(new Error('Project host not set'));
        }
        // Haven't inlined the code here so that we can keep Git history on the implementation, just in case
        return saveProjectToServer(host, projectId, vmState, params);
    }

    private cacheDefaultProject (storage: ScratchStorage) {
        const defaultProjectAssets = defaultProject(this.translator);
        defaultProjectAssets.forEach(asset => storage.builtinHelper._store(
            storage.AssetType[asset.assetType],
            storage.DataFormat[asset.dataFormat],
            asset.data,
            asset.id
        ));
    }

    private addOfficialScratchWebStores (storage: ScratchStorage) {
        storage.addWebStore(
            [storage.AssetType.Project],
            this.getProjectGetConfig.bind(this),
            this.getProjectCreateConfig.bind(this),
            this.getProjectUpdateConfig.bind(this)
        );

        storage.addWebStore(
            [storage.AssetType.ImageVector, storage.AssetType.ImageBitmap, storage.AssetType.Sound],
            this.getAssetGetConfig.bind(this)
        );
    }

    private getProjectGetConfig (projectAsset) {
        const path = `${this.projectHost}/${projectAsset.assetId}`;
        const qs = this.projectToken ? `?token=${this.projectToken}` : '';
        return path + qs;
    }

    private getProjectCreateConfig () {
        return {
            url: `${this.projectHost}/`,
            withCredentials: true
        };
    }

    private getProjectUpdateConfig (projectAsset: Asset) {
        return {
            url: `${this.projectHost}/${projectAsset.assetId}`,
            withCredentials: true
        };
    }

    private getAssetGetConfig (asset: Asset) {
        if (!/^https?:\/\//.test(this.assetHost ?? '')) {
            return null;
        }
        return `${this.assetHost}/internalapi/asset/${asset.assetId}.${asset.dataFormat}/get/`;
    }
}
